import React from "react";
import intl from "react-intl-universal";

import {TableDataSource} from "../dataSources/TableDataSource";
import {FilterSchema} from "../dataSources/DataSource";
import {ColId, ColSchema, Row} from "../../ui/components/Table";
import {GQLResponse} from "../gqlModel/types";
import {BasicDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {queryGqlApi} from "../../graphql/queryGqlApi";
import {listAuditAssignments} from "../../graphql/audit/queries";
import {
    onCreateAuditAssignment,
    onUpdateAuditAssignment,
    onDeleteAuditAssignment,
} from "../../graphql/audit/subscriptions";
import {Button} from "../../ui/components/Button/Button";
import {getMuiIcon} from "../../ui/components/icons/createMuiIcon";
import {AssigneeTag} from "../../ui/components/AssigneeTag/AssigneeTag";
import {HoverCard} from "../../ui/components/HoverCard/HoverCard";
import {TaskStatusMarker} from "../../ui/components/TaskStatusMarker/TaskStatusMarker";
import {TaskOverdueDetailsCard} from "../../ui/components/TaskStatusMarker/TaskOverdueDetailsCard";
import {AuditAssignmentStatusBadge} from "../../ui/screens/Audits/AuditAssignmentStatusBadge";
import {AuditAssignment} from "./types";
import {snackBarRef} from "../snackbarRef";

const ENDPOINT = "pgServer";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// An assignment is only "overdue" while it's still actionable - once it's
// completed or cancelled, a past due date is no longer a problem to flag.
const isAssignmentOverdue = (assignment: AuditAssignment): boolean =>
    !!assignment.dueAt
    && new Date(assignment.dueAt).getTime() < Date.now()
    && (assignment.status === 'assigned' || assignment.status === 'in_progress');

const colOrder: ColId[] = ['audit', 'location', 'assignee', 'status', 'dueAt', 'actions'];

const colSchema: ColSchema = {
    audit: {
        getTitle: () => intl.get('audits.assignments.colAudit').d('Audit'),
        alignLeft: true,
        minWidth: 220,
    },
    location: {
        getTitle: () => intl.get('audits.assignments.colLocation').d('Location'),
        alignLeft: true,
        minWidth: 160,
    },
    assignee: {
        getTitle: () => intl.get('audits.assignments.colAssignee').d('Assignee'),
        alignLeft: true,
        minWidth: 160,
    },
    status: {
        getTitle: () => intl.get('audits.assignments.colStatus').d('Status'),
        alignLeft: true,
        minWidth: 130,
        maxWidth: 170,
    },
    dueAt: {
        getTitle: () => intl.get('audits.assignments.colDue').d('Due'),
        alignLeft: true,
        minWidth: 120,
        maxWidth: 160,
    },
    actions: {
        getTitle: () => '',
        alignLeft: true,
        minWidth: 150,
        maxWidth: 190,
    },
};

const statusLabel = (status: AuditAssignment['status'], audience: "admin" | "auditor") =>
    <AuditAssignmentStatusBadge status={status} size="sm" audience={audience}/>;

export class AuditAssignmentDS extends TableDataSource<AuditAssignment> {
    readonly _modelName = 'AuditAssignment' as const;
    readonly wsId: string;

    readonly _subscriptionGQLs = {
        onCreate: onCreateAuditAssignment,
        onUpdate: onUpdateAuditAssignment,
        onDelete: onDeleteAuditAssignment,
    };

    getAuditName?: (id: string) => string | undefined;
    getLocationName?: (id?: string | null) => string | undefined;
    getUserName?: (id: string) => string | undefined;
    getRoleName?: (id: string) => string | undefined;
    onStartClick?: (assignment: AuditAssignment) => void;
    onEditClick?: (assignment: AuditAssignment) => void;
    showStartAction = true;
    // Set by AssignedAuditsScreen to 'auditor' - see AuditAssignmentStatusBadge
    // for why the "assigned" status reads differently for the person who owns
    // the work vs. the admin who handed it off.
    statusAudience: "admin" | "auditor" = "admin";

    constructor(deps: BasicDataSourceDeps, wsId: string) {
        super(deps);
        this.wsId = wsId;
        // Sorted (and grouped, see mapPageToTableRows) by audit by default, so every
        // assignment for the same audit reads as one cluster instead of a shuffled
        // list where its name repeats at random positions down the page.
        this.setSortRule('audit');
    }

    get syncHash() { return this.wsId; }
    get syncQueryFilter() { return {workspaceId: this.wsId}; }

    // 'audit' has no raw field on AuditAssignment (only auditTemplateId) - it's a
    // display value resolved via getAuditName, same as mapResultToTableRow's own
    // 'audit' cell. Without this, sorting/grouping by it would silently no-op.
    getFilterableValue = (field: string, obj: AuditAssignment): string | number | boolean | null | undefined => {
        if (field === 'audit') return this.getAuditName?.(obj.auditTemplateId) ?? obj.auditTemplateId;
        return (obj as any)[field];
    };

    getFilterSchema(): FilterSchema<AuditAssignment> {
        return {
            audit: {
                type: 'client',
                apply: (assignment) => {
                    const needle = (this.filterValues.audit ?? '').toLowerCase();
                    const audit = this.getAuditName?.(assignment.auditTemplateId) ?? assignment.auditTemplateId;
                    const location = this.getLocationName?.(assignment.locationId) ?? '';
                    const user = (assignment.assignedUserId ? this.getUserName?.(assignment.assignedUserId) : undefined) ?? '';
                    const role = (assignment.assignedRoleId ? this.getRoleName?.(assignment.assignedRoleId) : undefined) ?? '';
                    return `${audit} ${location} ${user} ${role}`.toLowerCase().includes(needle);
                },
            },
            status: {
                type: 'client',
                apply: (assignment) => {
                    const value = this.filterValues.status;
                    if (!value || value === 'all') return true;
                    if (value === 'active') return assignment.status === 'assigned' || assignment.status === 'in_progress';
                    return assignment.status === value;
                },
            },
            // Only set by AssignedAuditsScreen (the non-admin view) - the admin's
            // AuditAssignmentsPanel leaves this unset so it keeps seeing everyone's
            // assignments. A role assignment has no single owner, so any teammate
            // with that role sees it too (shared pool, first to start it takes it).
            mine: {
                type: 'client',
                apply: (assignment) => {
                    const value = this.filterValues.mine as {userId: string; roleIds: string[]} | null | undefined;
                    if (!value) return true;
                    if (assignment.assignedUserId) return assignment.assignedUserId === value.userId;
                    if (assignment.assignedRoleId) return value.roleIds.includes(assignment.assignedRoleId);
                    return false;
                },
            },
        };
    }

    getFilterDefaultValues() { return {audit: '', status: 'all', mine: null}; }

    loadGQLFetch(): Promise<GQLResponse<AuditAssignment>> {
        return queryGqlApi(
            listAuditAssignments, {workspaceId: this.wsId},
            this.deps.dataClientService, this.deps.authService, ENDPOINT,
        ) as Promise<GQLResponse<AuditAssignment>>;
    }

    loadMoreGQLFetch(nextToken: string): Promise<GQLResponse<AuditAssignment>> {
        return queryGqlApi(
            listAuditAssignments, {workspaceId: this.wsId, nextToken},
            this.deps.dataClientService, this.deps.authService, ENDPOINT,
        ) as Promise<GQLResponse<AuditAssignment>>;
    }

    getWsClient() {
        return this.deps.dataClientService.getWsClientByKey(ENDPOINT);
    }

    getColsSchema(): ColSchema { return colSchema; }
    getColOrder(): ColId[] { return colOrder; }

    // assigneeSize lets callers request the compact AssigneeTag variant (see
    // AssigneeTag.tsx) without duplicating this mapping — the mobile/tablet
    // card in AuditAssignmentsPanel passes "sm" to match Overview's card,
    // the desktop table (via mapPageToTableRows) keeps the default "md".
    mapResultToTableRow = (assignment: AuditAssignment): Row => this.mapAssignmentToRow(assignment, "md");

    mapAssignmentToRow = (assignment: AuditAssignment, assigneeSize: "md" | "sm"): Row => {
        // A role assignment has no one specific person yet - it's a shared pool an
        // auditor claims by starting it (see startAssignedAuditRun's mock resolver,
        // which sets assignedUserId on start), so it's just as startable here as a
        // personal assignment.
        const canStart = !!(assignment.assignedUserId || assignment.assignedRoleId) && (assignment.status === 'assigned' || assignment.status === 'in_progress');
        const assignee = assignment.assignedUserId
            ? <AssigneeTag kind="user" name={this.getUserName?.(assignment.assignedUserId) ?? assignment.assignedUserId} size={assigneeSize}/>
            : assignment.assignedRoleId
                ? <AssigneeTag kind="role" name={this.getRoleName?.(assignment.assignedRoleId) ?? assignment.assignedRoleId} size={assigneeSize}/>
                : '—';
        const overdue = isAssignmentOverdue(assignment);
        const dueDate = assignment.dueAt ? new Date(assignment.dueAt) : null;
        const daysOverdue = overdue ? Math.max(1, Math.round((Date.now() - dueDate!.getTime()) / MS_PER_DAY)) : 0;
        // Only the admin view can nudge someone else - the auditor's own
        // AssignedAuditsScreen reuses this same DS (statusAudience: 'auditor')
        // to show their own overdue work, where "remind" has no meaning.
        const assigneeName = assignment.assignedUserId
            ? this.getUserName?.(assignment.assignedUserId)
            : assignment.assignedRoleId
                ? this.getRoleName?.(assignment.assignedRoleId)
                : undefined;
        const remindAction = this.statusAudience === 'admin' && assigneeName ? {
            label: intl.get('audits.assignments.remind').d('Remind'),
            sentLabel: intl.get('audits.assignments.reminderSent').d('Reminder sent'),
            onClick: () => {
                snackBarRef.current?.enqueueSnackbar({
                    variant: 'success',
                    message: intl.get('audits.assignments.reminderSentTo', {name: assigneeName}).d('Reminder sent to {name}'),
                });
            },
        } : undefined;
        return {
            ...assignment,
            audit: this.getAuditName?.(assignment.auditTemplateId) ?? assignment.auditTemplateId,
            location: this.getLocationName?.(assignment.locationId) ?? '—',
            assignee,
            status: statusLabel(assignment.status, this.statusAudience),
            dueAt: dueDate ? (
                overdue ? (
                    // The whole pill (date text + icon) is the HoverCard's anchor, not just
                    // the icon - anchoring to the icon alone put the card's left edge hard
                    // against a ~16px target near the column's right side, so a 272px-wide
                    // card had nowhere to go but sideways past the table edge. Anchoring to
                    // the full pill also gives touch (tablet/mobile swap hover for tap, see
                    // HoverCard's isTouch branch) a properly sized trigger zone instead of a
                    // 16x16 one.
                    <HoverCard
                        content={
                            <TaskOverdueDetailsCard
                                footerHintReadonly={intl.get('audits.assignments.overdueFooter').d('Still open — can be completed late')}
                                occurrences={[{date: dueDate, daysOverdue}]}
                                remindAction={remindAction}
                            />
                        }
                    >
                        <span
                            className="Audits-dueCell Audits-dueCell--overdue"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {dueDate.toLocaleDateString()}
                            <TaskStatusMarker variant="overdue"/>
                        </span>
                    </HoverCard>
                ) : (
                    <span className="Audits-dueCell">{dueDate.toLocaleDateString()}</span>
                )
            ) : '—',
            actions: (
                <div className="Audits-rowActions" onClick={(event) => event.stopPropagation()}>
                    {this.onEditClick && (
                        <Button
                            className="Audits-rowActions__edit"
                            type="neutral"
                            style="plain"
                            size="sm"
                            Icon={getMuiIcon("edit")}
                            text={intl.get('common.edit').d('Edit')}
                            onClick={() => this.onEditClick?.(assignment)}
                        />
                    )}
                    {this.showStartAction && (
                        <Button
                            type="primary"
                            style="inline"
                            size="sm"
                            Icon={getMuiIcon("play_arrow")}
                            text={assignment.status === 'in_progress'
                                ? intl.get('audits.assignments.continue').d('Continue')
                                : intl.get('audits.assignments.start').d('Start')}
                            isDisable={!canStart}
                            onClick={() => this.onStartClick?.(assignment)}
                        />
                    )}
                </div>
            ),
            raw: assignment,
        };
    };

    // The audit name gets its own row ahead of the group's assignment rows (mirrors
    // AuditProgressTable's Overview grouping - see that component for the full
    // rationale) rather than repeating on every one of them. Only meaningful while
    // sorted by audit - any other sort scatters same-audit rows apart, so this falls
    // back to the plain 1-row-per-assignment mapping rather than showing misleading
    // isolated group headers.
    mapPageToTableRows = (items: AuditAssignment[]): Row[] => {
        if (this.sortRule?.field !== 'audit') return items.map(this.mapResultToTableRow);
        const rows: Row[] = [];
        items.forEach((assignment, i) => {
            const groupStart = i === 0 || items[i - 1].auditTemplateId !== assignment.auditTemplateId;
            if (groupStart) {
                rows.push({
                    id: `${assignment.auditTemplateId}:name`,
                    isNameRow: true,
                    audit: this.getAuditName?.(assignment.auditTemplateId) ?? assignment.auditTemplateId,
                    location: <></>, assignee: <></>, status: <></>, dueAt: <></>, actions: <></>,
                });
            }
            rows.push({...this.mapResultToTableRow(assignment), audit: <></>});
        });
        return rows;
    };

    get activeAssignments(): AuditAssignment[] {
        return this.allRows.filter((assignment) => assignment.status === 'assigned' || assignment.status === 'in_progress');
    }

    upsertAuditAssignment(assignment: AuditAssignment) {
        this._upsertItem(assignment, true);
    }

    removeAuditAssignment(id: string) {
        this._removeItem(id);
    }
}
